import type { ProductResponse } from "@/app/api/types/product";
import type { UnitResponse } from "@/app/api/types/unit";

/** Quantity used to compute the default flow weight at creation time. */
const CREATION_DEFAULT_QUANTITY = 1;

export interface ProductDefaults {
  /**
   * Null when the new flow is created without a product binding — e.g. the
   * transport-mode "add row" via the sender picker, where the recipient
   * (the overloaded `product_id`) is filled in by a later cell edit.
   */
  product_id: string | null;
  product: ProductResponse | null;
  unit_id: string | null;
  unit: UnitResponse | null;
  price_per_weight: number;
  /**
   * Set when the new flow is created from the sender picker (transport).
   * Carried into the optimistic flow row by the modify buffer.
   */
  partner_id?: string | null;
  /**
   * Custom (non-DB) sender entity, carried on `flow_metadata.custom_partner`.
   * Only set when the user picked a custom sender from the transport new-row.
   */
  custom_partner?: Record<string, unknown> | null;
  /**
   * Omitted when the new product has no usable weight source (no junction
   * weight, no box/piece weight, or all are 0). Callers that do partial
   * updates (e.g. product change) should leave the existing flow weight
   * untouched rather than overwriting with 0.
   */
  weight?: number;
  /** When provided (e.g. adding from history), overrides the default quantity of 0. */
  quantity?: number;
  /**
   * Subset of `flow.flow_metadata` to carry over (e.g. custom fields when
   * re-adding a past flow from history). Merged on top of the new-flow
   * marker by `performFlowCreate`. Caller is responsible for filtering out
   * system keys (AI suggestions/telemetry, NEW_FLOW_MARKER).
   */
  flow_metadata?: Record<string, unknown>;
}

export interface CurrentFlowContext {
  product: ProductResponse | null;
  unit: UnitResponse | null;
  quantity: number | null;
  weight: number | null;
}

/**
 * Resolve default unit/price/weight for a product.
 * Pure function — used by both flow creation (EmptyProductRow) and product change (ProductCell).
 *
 * When `currentFlow` is provided, weight resolution becomes "sticky":
 * the existing weight is preserved when it differs from the computed
 * default for the current product/unit/qty (i.e. user-edited), and also
 * preserved when the new product has no usable weight source.
 *
 * Unit resolution: the new product's default unit is always preferred.
 * When the new product has no default unit and `currentFlow` is provided
 * (product change), the current unit is kept instead of being reset to
 * empty. On the creation path (no `currentFlow`) it falls back to the
 * product's first available unit, then null.
 */
export function resolveProductDefaults({
  productId,
  product,
  products,
  getEffectivePrice,
  partnerId,
  skipPricePrefill = false,
  currentFlow,
}: {
  productId: string;
  product: ProductResponse | null | undefined;
  products: ProductResponse[];
  getEffectivePrice: (
    productId: string,
    partnerId: string | null,
  ) => number | null;
  partnerId: string | null;
  /** When true, do not prefill price_per_weight (e.g. vivo/M3 export mode) */
  skipPricePrefill?: boolean;
  /**
   * Context of the flow being changed. When provided, weight is preserved
   * if the user previously edited it, or if the new product has no usable
   * weight default.
   */
  currentFlow?: CurrentFlowContext;
}): ProductDefaults {
  const foundProduct =
    products.find((p) => p.id === productId) || product || null;

  const productUnitOptions = foundProduct?.units || [];
  const defaultUnitOption =
    productUnitOptions.find((u) => u.is_default) ?? null;

  let resolvedUnitId: string | null;
  let resolvedUnit: UnitResponse | null;
  if (defaultUnitOption) {
    // The product defines a default unit — always prefer it.
    resolvedUnitId = defaultUnitOption.unit_id ?? null;
    resolvedUnit = defaultUnitOption.unit ?? null;
  } else if (currentFlow) {
    // Product change with no default unit: keep the current unit rather than
    // resetting it to empty. Falls back to the first available unit when the
    // flow had no unit yet.
    const fallbackOption = productUnitOptions[0] ?? null;
    resolvedUnitId = currentFlow.unit?.id ?? fallbackOption?.unit_id ?? null;
    resolvedUnit = currentFlow.unit ?? fallbackOption?.unit ?? null;
  } else {
    // Creation path: no current unit to preserve, fall back to the first
    // available unit (or null when the product has none).
    const fallbackOption = productUnitOptions[0] ?? null;
    resolvedUnitId = fallbackOption?.unit_id ?? null;
    resolvedUnit = fallbackOption?.unit ?? null;
  }

  let pricePerWeight = 0;
  if (!skipPricePrefill) {
    const effectivePrice = foundProduct
      ? getEffectivePrice(productId, partnerId)
      : null;
    pricePerWeight = effectivePrice ?? foundProduct?.weight_price ?? 0;
  }

  const resolvedWeight = currentFlow
    ? resolveStickyWeight(currentFlow, foundProduct, resolvedUnit)
    : computeDefaultFlowWeight(
        foundProduct,
        resolvedUnit,
        CREATION_DEFAULT_QUANTITY,
      );

  return {
    product_id: productId,
    product: foundProduct,
    unit_id: resolvedUnitId,
    unit: resolvedUnit,
    price_per_weight: pricePerWeight,
    ...(resolvedWeight == null ? {} : { weight: resolvedWeight }),
  };
}

/**
 * Single source of truth for default flow weight. Used by:
 * - Flow creation (EmptyProductRow) via resolveProductDefaults with qty=1
 * - Product change (sticky comparison) via resolveStickyWeight at the flow's
 *   current qty
 * - Qty-driven recompute (QuantityCell)
 *
 * Rules (returns null when no positive default exists):
 * 1. Unit is non-per_box with positive junction → qty * junction
 *    (qty <= 0 returns raw junction so the sticky comparison against a
 *    newly-created flow (qty=0, weight=junction) stays aligned)
 * 2. Unit is per_box with positive junction → junction (flat, not qty-scaled)
 * 3. Unit is per_box without junction → box_weight
 * 4. Unit exists (non-per_box, no junction) → piece_weight
 * 5. No unit → prefer positive box_weight, else piece_weight
 */
export function computeDefaultFlowWeight(
  product: ProductResponse | null | undefined,
  unit: UnitResponse | null | undefined,
  quantity: number = 0,
): number | null {
  if (!product) return null;

  const junction = getJunctionWeight(product, unit);

  if (unit && !unit.per_box) {
    if (junction != null) return quantity <= 0 ? junction : quantity * junction;
    return positive(product.piece_weight);
  }

  if (unit?.per_box) {
    return junction ?? positive(product.box_weight);
  }

  return positive(product.box_weight) ?? positive(product.piece_weight);
}

/**
 * Positive junction weight (`product_units.weight`) for a given unit, or null.
 * Shared lookup used by `computeDefaultFlowWeight` and by cells that auto-fill
 * weight on unit/qty change (UnitCell, QuantityCell).
 */
export function getJunctionWeight(
  product: ProductResponse | null | undefined,
  unit: UnitResponse | null | undefined,
): number | null {
  if (!product || !unit) return null;
  return positive(product.units?.find((u) => u.unit_id === unit.id)?.weight);
}

function positive(value: number | null | undefined): number | null {
  return value != null && value > 0 ? value : null;
}

/**
 * Sticky weight resolution for product change.
 * - If the current flow's weight differs from its own computed default
 *   (user edited it), preserve it (return null → caller omits).
 * - Else if the new product's default is null/0, preserve (return null).
 * - Else return the new product's default at the current quantity.
 */
function resolveStickyWeight(
  currentFlow: CurrentFlowContext,
  newProduct: ProductResponse | null,
  newUnit: UnitResponse | null,
): number | null {
  const qty = currentFlow.quantity ?? 0;
  const oldDefault = computeDefaultFlowWeight(
    currentFlow.product,
    currentFlow.unit,
    qty,
  );
  const userEdited = !weightsEqual(currentFlow.weight, oldDefault);
  if (userEdited) return null;

  const newDefault = computeDefaultFlowWeight(newProduct, newUnit, qty);
  if (newDefault == null || newDefault === 0) return null;
  return newDefault;
}

/** Compare two weight values with 3-decimal rounding (matches QuantityCell preview). */
export function weightsEqual(
  a: number | null | undefined,
  b: number | null | undefined,
): boolean {
  return Math.round((a ?? 0) * 1000) === Math.round((b ?? 0) * 1000);
}
